import { Box, Button, Card, CardContent, Chip, Grid, Stack, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab';
import { AccountTreeOutlined, ScheduleOutlined, Sync } from '@mui/icons-material';
import ThirdPartyConnectionDetailsService from '../../shared-services/third-party-connection-details/third-party-connection.service';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { IThirdPartyConnectionDetails } from '../../shared-services/third-party-connection-details/third-party-connection-details.model';
import { ConnectionButton } from '../../shared-components/components/connection-button';
import { ShallowFormDisplay } from '../../../../components/presentation-components/form-field-components/general/shallow-form-display';
import { InformationBox } from '../../../../components/presentation-components/miscellaneous-components/information-box';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import { RevokeTokenButton } from './revoke-token-button';
import { LoadingPage } from '../../../../components/pages/loading-page';
import { ScheduleSyncDialog } from '../../../../components/dialogs/settings/schedule-sync-dialog';
import { AccountingSyncProvider } from '../../../../shared/services/schedule/schedule.model';
import { UserDetailsContext } from '../../../../context/user-details/user-details';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import { Link, useNavigate } from 'react-router-dom';
import { SelectTenantDialog } from '../../../../components/dialogs/select-tenant-dialog';
import { IThirdPartyConnectionDetailsCompany } from '../../shared-services/third-party-connection-details/third-party-connection-details.model';
import { ledgerSyncNavigation } from '../../../../shared/utils/ledger-sync-utils';

const SYNCABLE_PROVIDERS: AccountingSyncProvider[] = ['xero', 'sage', 'quickbooks', 'sage200'];

export const Details: FunctionComponent<{ provider: string }> = ({ provider }) => {
  const thirdPartyConnectionDetailsService = new ThirdPartyConnectionDetailsService();
  const { userDetails } = useContext(UserDetailsContext);
  const userNotificationsContext = useContext(UserNotificationsContext);
  const reportPeriodContext = useContext(ReportPeriodContext);
  const navigate = useNavigate();
  const isAdmin = userDetails?.roles?.includes('admin') ?? false;
  const [connectionDetails, setConnectionDetails] = useState<IThirdPartyConnectionDetails>();
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [connections, setConnections] = useState<any>()
  const [hasNeverConnected, setHasNeverConnected] = useState<boolean>(false)
  const [errorText, setErrorText] = useState<string>()
  const [pageReady, setPageReady] = useState<boolean>(false)
  const [openScheduleSync, setOpenScheduleSync] = useState<boolean>(false)
  // Start sync: the companies to pick from arrive when the button is pressed, then the same
  // dialog the Financial page uses (company, and optionally a date to read again from).
  const [syncCompanies, setSyncCompanies] = useState<IThirdPartyConnectionDetailsCompany[]>()
  const [isLoadingSyncCompanies, setIsLoadingSyncCompanies] = useState<boolean>(false)

  const isSyncableProvider = SYNCABLE_PROVIDERS.includes(provider as AccountingSyncProvider);
  const hasActiveConnection = Array.isArray(connections) && connections.some((c: any) => c.connected);
  const canScheduleSync = isAdmin && isSyncableProvider && hasActiveConnection;
  const canStartSync = canScheduleSync;
  const selectedReportPeriod = reportPeriodContext.allReportPeriods?.find(period => period.id === reportPeriodContext.lastSelectedReportId);

  const startSyncHandler = () => {
    if (isLoadingSyncCompanies) return;
    setIsLoadingSyncCompanies(true);
    thirdPartyConnectionDetailsService.getConnectedCompanies(provider)
      .then(response => {
        if (!response || response.length === 0) {
          userNotificationsContext.openSnackbar({ open: true, message: 'You have no companies associated with this connection.', backgroundColor: 'text.primary' });
          return;
        }
        setSyncCompanies(response);
      })
      .catch(() => {
        userNotificationsContext.openSnackbar({ open: true, message: 'The companies could not be read from this integration. Please try again.', backgroundColor: 'text.primary' });
      })
      .finally(() => setIsLoadingSyncCompanies(false));
  };

  // The sync itself runs on the Financial page, which the user is taken to straight away: it
  // shows the records arriving and the coverage of what the sync did, as its own Sync Data does.
  const submitSyncHandler = (connectionCode: string, companyId: string, resetForm: () => void, syncFrom: string | null) => {
    resetForm();
    setSyncCompanies(undefined);
    const { to, state } = ledgerSyncNavigation({ provider: connectionCode, companyId, syncFrom });
    navigate(to, { state });
  };
  // Mapping the chart of accounts needs a live connection to read it from, and the same
  // provider set that syncs a ledger in the first place.
  const canMapChartOfAccounts = isSyncableProvider && hasActiveConnection;
  // Mapping happens on the Chart of Accounts page. The chart is stored (every sync refreshes it),
  // so the plain page lists it; when it has never been read, the page's own button reads it.
  const chartOfAccountsLink = '/settings/data-upload/chart-of-accounts';

  useEffect(() => {
    thirdPartyConnectionDetailsService.getConnection(`${provider}`)
      .then(response => {
        setConnectionDetails(response);
        if (!response) {
          setPageReady(true)
        }
      })
      .catch(err => {
        setPageReady(true)
        console.log(err)
      }
      )
  }, [])

  useEffect(() => {
    if (connectionDetails) {
      thirdPartyConnectionDetailsService.checkConnection(provider)
        .then(response => {
          setPageReady(true)
          setIsConnected(true)
          setConnections(response.connections)
          if (response.code === 4) {
            setErrorText('You have no companies associated with this connection.')
          }
        })
        .catch(err => {
          setPageReady(true)
          if (err.response.data.code === 1) {
            setHasNeverConnected(true)
          }
          if (err.response.data.code === 5) {
            setErrorText('It is not possible to connect to this integration')
          }
          if (err.response.data.code === 6) {
            setErrorText('There has been an error while trying to connect you to this integration, please try again')
          }
        })
    }
  }, [connectionDetails])



  return (
    <LoadingPage pageReady={pageReady}>
      <>
        {!connectionDetails &&
          <InformationBox
            icon={IconName.ErrorOutline}
            text='It is not possible to connect to this integration'
          />}
        {connectionDetails &&
          <>
            {!connections &&
              <ShallowFormDisplay name={`Connection`} value={hasNeverConnected ?
                <Chip label='Connection Not Established' variant="outlined" color='error' sx={{ color: 'red' }} /> :
                <Chip label='Not Active' variant="outlined" color='error' sx={{ color: 'red' }} />}
                isTopRow={true} isBottomRow={true}></ShallowFormDisplay>
            }
            {connections && connections.map((connection: any, index: number) => (
              <ShallowFormDisplay name={`Connection ${connection.companyName}`} value={connection.connected ?
                <Chip label='Active' variant="outlined" color='success' sx={{ color: 'green' }} /> :
                <Chip label='Not Active' variant="outlined" color='error' sx={{ color: 'red' }} />}
                isTopRow={index === 0} isBottomRow={index === connections.length - 1}></ShallowFormDisplay>
            ))}
            {errorText && <Grid container sx={{ mt: 2 }}>
              <Grid item xs={12}>
                <InformationBox
                  text={errorText}
                  icon={IconName.ErrorOutline}
                />
              </Grid>
            </Grid>}
            {canMapChartOfAccounts && (
              <Card variant='outlined' sx={{ borderColor: 'lightGrey.main', borderRadius: '10px', boxShadow: 'none', mt: 3 }}>
                <CardContent>
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent='space-between' alignItems={{ xs: 'flex-start', md: 'center' }} gap={2}>
                    <Stack spacing={1}>
                      <Typography variant='h6' sx={{ color: 'text.primary' }}>Chart of Accounts</Typography>
                      <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: '640px' }}>
                        Give each account code an outcome once and every sync classifies itself, instead of
                        relying on supplier names. Codes left without an outcome keep classifying by supplier.
                      </Typography>
                    </Stack>
                    <Button
                      variant='outlined'
                      component={Link}
                      to={chartOfAccountsLink}
                      startIcon={<AccountTreeOutlined fontSize='small' />}>
                      <Typography sx={{ pl: 0.5 }}>Map account codes</Typography>
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            )}
            <Box display='flex' sx={{ flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mt: 2, mb: 2 }}>
              {canStartSync && (
                <LoadingButton
                  variant='outlined'
                  loading={isLoadingSyncCompanies}
                  loadingPosition='start'
                  startIcon={<Sync />}
                  onClick={startSyncHandler}
                >
                  Start sync
                </LoadingButton>
              )}
              {canScheduleSync && (
                <Button
                  variant='outlined'
                  startIcon={<ScheduleOutlined />}
                  onClick={() => setOpenScheduleSync(true)}
                >
                  Schedule sync
                </Button>
              )}
              {isConnected && <RevokeTokenButton provider={provider} />}
              <ConnectionButton isConnected={isConnected} connectionDetails={connectionDetails} provider={provider}></ConnectionButton>
            </Box>
          </>
        }
        {canStartSync && syncCompanies && connectionDetails && (
          <SelectTenantDialog
            open={!!syncCompanies}
            isSubmitting={false}
            closeDialogHandler={() => setSyncCompanies(undefined)}
            submitHandler={submitSyncHandler}
            dialogDetails={syncCompanies}
            connectionCode={provider}
            connectionName={connectionDetails.name}
            periodStartDate={selectedReportPeriod?.startDate}
            periodEndDate={selectedReportPeriod?.endDate}
          />
        )}
        {canScheduleSync && (
          <ScheduleSyncDialog
            open={openScheduleSync}
            onClose={() => setOpenScheduleSync(false)}
            provider={provider as AccountingSyncProvider}
            providerName={connectionDetails?.name}
          />
        )}
      </>
    </LoadingPage>
  )
}